import React, { useState } from "react";
import {
  ShoppingCart,
  X,
  AlertCircle,
  CheckLine,
  Loader,
  ArrowRight,
  Share2,
} from "lucide-react";
import { IProduct, ICompatibilityResponse } from "../types";
import { toNumber, formatPrice } from "../utils/price";
import { PricingDetailsModal } from "./PricingDetailsModal";
import { ShareBuildModal } from "./ShareBuildModal";
import { apiService } from "../services/api";

interface BuildSummaryProps {
  selectedComponents: Record<string, IProduct>;
  onRemoveComponent: (categoryId: string) => void;
  compatibilityData?: ICompatibilityResponse | null;
  isCheckingCompatibility?: boolean;
  onLoadBuild?: (components: Record<string, IProduct>) => void;
}

export const BuildSummary: React.FC<BuildSummaryProps> = ({
  selectedComponents,
  onRemoveComponent,
  compatibilityData,
  isCheckingCompatibility,
  onLoadBuild,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const componentEntries = Object.entries(selectedComponents);
  const componentCount = componentEntries.length;

  // Calculate adapter prices if needed - use discounted price if available
  let adapterTotal = 0;
  if (compatibilityData) {
    if (compatibilityData.wheelbaseAdapter) {
      const price =
        compatibilityData.wheelbaseAdapter.discounted_price ||
        compatibilityData.wheelbaseAdapter.price;
      adapterTotal += toNumber(price);
    }
    if (compatibilityData.wheelAdapter) {
      const price =
        compatibilityData.wheelAdapter.discounted_price ||
        compatibilityData.wheelAdapter.price;
      adapterTotal += toNumber(price);
    }
  }

  const totalPrice =
    componentEntries.reduce(
      (sum, [_, product]) => sum + toNumber(product.price),
      0,
    ) + adapterTotal;

  const handleContinue = async () => {
    // Track build snapshot when user clicks Continue
    const components = componentEntries.map(([categoryId, product]) => ({
      productId: String(product.id),
      productName: product.name,
      brandName:
        product.brand?.name || (product as any).manufacturer || "Unknown",
      category: categoryId,
      price: toNumber(product.price),
    }));

    // Add adapters to components if they exist
    if (compatibilityData?.wheelbaseAdapter) {
      components.push({
        productId: String(compatibilityData.wheelbaseAdapter.id),
        productName: compatibilityData.wheelbaseAdapter.name,
        brandName: compatibilityData.wheelbaseAdapter.brand.name,
        category: "adapter",
        price: toNumber(
          compatibilityData.wheelbaseAdapter.discounted_price ||
            compatibilityData.wheelbaseAdapter.price,
        ),
      });
    }

    if (compatibilityData?.wheelAdapter) {
      components.push({
        productId: String(compatibilityData.wheelAdapter.id),
        productName: compatibilityData.wheelAdapter.name,
        brandName: compatibilityData.wheelAdapter.brand.name,
        category: "adapter",
        price: toNumber(
          compatibilityData.wheelAdapter.discounted_price ||
            compatibilityData.wheelAdapter.price,
        ),
      });
    }

    await apiService.trackBuildSnapshot(components, totalPrice);
    setModalOpen(true);
  };

  const getProductImage = (product: IProduct): string | null => {
    // Handle images array
    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      const primaryImage =
        product.images.find((img) => img.is_primary) || product.images[0];
      return primaryImage?.image || null;
    }
    // Fallback to single image property if images array doesn't exist
    return (product as any).image || null;
  };

  const isDarkMode =
    document.querySelector(".sim-rig-builder")?.getAttribute("data-theme") ===
    "dark";
  const iconClasses = isDarkMode
    ? "srb-w-[100px] srb-brightness-0 srb-invert"
    : "srb-w-[100px] srb-brightness-0";

  if (componentCount === 0) {
    return (
      <div className="lg:srb-px-6 srb-border-2 srb-border-secondary srb-sticky srb-top-6">
        <div className="srb-flex srb-items-center srb-gap-3 srb-mb-6">
          <ShoppingCart className="srb-text-primary" size={24} />
          <h2 className="srb-text-2xl srb-m-0 srb-font-bold srb-text-main">
            Build Summary
          </h2>
        </div>
        <div className="srb-text-center srb-py-12">
          <p className="srb-text-main srb-bg-hover srb-py-6">
            Start building by selecting components
          </p>
        </div>
        <div className="srb-mt-4">
          <button
            className="srb-cursor-pointer srb-flex srb-items-center srb-justify-center srb-gap-2 srb-border srb-border-subtle !srb-bg-transparent srb-text-main srb-w-full srb-py-3 hover:srb-bg-hover srb-font-semibold srb-transition"
            onClick={() => setShareModalOpen(true)}
          >
            <Share2 size={18} />
            Load a Saved Build
          </button>
        </div>
        <div className="srb-mt-6 srb-pt-6 srb-border-t srb-border-secondary srb-text-center">
          <p className="srb-text-main srb-flex srb-items-center srb-justify-center srb-gap-4 srb-mt-1 srb-text-sm srb-italic srb-font-semibold">
            Powered by{" "}
            <a
              href="https://build.readysetsim.com"
              target="_blank"
              rel="noopener noreferrer"
              className="srb-font-semibold srb-underline"
            >
              <img
                src="https://build.readysetsim.com/images/logos/rss_logo2.svg"
                alt="Ready Set Sim"
                className={iconClasses}
                draggable="false"
              />
            </a>
          </p>
        </div>

        <ShareBuildModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          selectedComponents={selectedComponents}
          onLoadBuild={onLoadBuild || (() => {})}
        />
      </div>
    );
  }

  return (
    <div className="lg:srb-px-6 srb-border-2 srb-border-secondary">
      <div className="srb-flex srb-items-center srb-gap-3 srb-mb-6">
        <ShoppingCart className="srb-text-primary" size={24} />
        <h2 className="srb-text-2xl srb-m-0 srb-font-bold srb-text-main">
          Build Summary
        </h2>
      </div>

      <div className="srb-space-y-3 srb-mb-6">
        {componentEntries.map(([categoryId, product]) => {
          const image = getProductImage(product);
          const price = toNumber(product.price);

          // Get dynamic border color from CSS variable
          const getBorderStyle = () => {
            const root = document.querySelector(".sim-rig-builder");
            if (!root) return {};
            const borderColor = getComputedStyle(root)
              .getPropertyValue("--srb-secondary-color")
              .trim();
            return { boxShadow: `0 0 0 1px ${borderColor}` };
          };

          return (
            <div
              key={categoryId}
              className="srb-p-3 srb-border srb-border-secondary"
              style={getBorderStyle()}
            >
              <div className="srb-flex srb-gap-2">
                <div className="srb-flex-1 srb-min-w-0">
                  <div className="srb-flex srb-justify-between srb-items-start srb-mb-1">
                    <div className="srb-flex-1 srb-min-w-0">
                      <div className="srb-text-xs srb-text-muted srb-uppercase srb-tracking-wider srb-mb-1">
                        {categoryId}
                      </div>
                      <div className="srb-flex srb-items-center srb-gap-2">
                        {image && (
                          <div className="srb-relative srb-w-12 srb-h-12 srb-flex-shrink-0 srb-bg-white srb-overflow-hidden">
                            <img
                              src={image}
                              alt={product.name}
                              className="srb-w-full srb-h-full srb-object-contain"
                            />
                          </div>
                        )}
                        <div className="srb-flex srb-flex-col">
                          <div className="srb-font-semibold srb-text-main srb-text-sm srb-text-wrap">
                            {product.name}
                          </div>
                          <div className="srb-text-xs srb-text-muted">
                            {product.brand?.name ||
                              (product as any).manufacturer ||
                              "Unknown"}
                          </div>
                          <div className="srb-font-bold srb-text-main srb-text-sm">
                            {formatPrice(product.price)}
                          </div>
                          {/* Discount Info */}
                          {(product.discount_code ||
                            product.discount_amount) && (
                            <div className="srb-mt-1 srb-flex srb-flex-wrap srb-gap-2">
                              {product.discount_amount && (
                                <span className="srb-inline-block srb-text-green-400 srb-text-xs srb-font-semibold">
                                  {product.discount_amount}
                                </span>
                              )}
                              {product.discount_code && (
                                <span className="srb-inline-block srb-text-primary srb-text-xs srb-font-mono">
                                  Code: {product.discount_code}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveComponent(categoryId)}
                      className="srb-cursor-pointer srb-border-none !srb-bg-transparent srb-text-main srb-transition srb-flex-shrink-0 hover:srb-text-primary"
                      title="Remove component"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Compatibility Status */}
        {(selectedComponents["wheel"] || selectedComponents["wheel-base"]) && (
          <div className="srb-mt-4">
            {isCheckingCompatibility ? (
              <div className="srb-p-3 srb-border srb-border-subtle srb-bg-hover">
                <div className="srb-flex srb-items-center srb-gap-2">
                  <Loader
                    className="srb-animate-spin srb-text-muted"
                    size={16}
                  />
                  <span className="srb-text-sm srb-text-muted">
                    Checking compatibility...
                  </span>
                </div>
              </div>
            ) : compatibilityData ? (
              <div>
                {compatibilityData.wheelbaseAdapter ||
                compatibilityData.wheelAdapter ? (
                  <div>
                    <div
                      className="srb-p-3 srb-border srb-border-yellow-600 srb-bg-background"
                      style={{ boxShadow: "0 0 0 1px #e4a436ff" }}
                    >
                      <div className="srb-flex srb-items-start srb-gap-2">
                        <AlertCircle
                          className="srb-text-yellow-500 srb-flex-shrink-0 srb-mt-0.5"
                          size={16}
                        />
                        <div>
                          <div className="srb-text-sm srb-font-semibold srb-text-main">
                            Adapter Required
                          </div>
                          <div className="srb-text-xs srb-text-main srb-mt-1">
                            {compatibilityData.message ||
                              "These components need an adapter to work together"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Display required adapters */}
                    {(compatibilityData.wheelbaseAdapter ||
                      compatibilityData.wheelAdapter) && (
                      <div className="srb-mt-2 srb-space-y-2">
                        {compatibilityData.wheelbaseAdapter && (
                          <div
                            className="srb-p-3 srb-border srb-border-secondary"
                            style={(function () {
                              const root =
                                document.querySelector(".sim-rig-builder");
                              if (!root) return {};
                              const borderColor = getComputedStyle(root)
                                .getPropertyValue("--srb-secondary-color")
                                .trim();
                              return { boxShadow: `0 0 0 1px ${borderColor}` };
                            })()}
                          >
                            <div className="srb-flex srb-gap-2">
                              <div className="srb-flex-1 srb-min-w-0">
                                <div className="srb-text-xs srb-text-yellow-400 srb-uppercase srb-tracking-wider srb-mb-1 srb-font-semibold">
                                  WHEELBASE ADAPTER
                                </div>
                                <div className="srb-flex srb-items-center srb-gap-2">
                                  {compatibilityData.wheelbaseAdapter
                                    .images[0] && (
                                    <div className="srb-relative srb-w-12 srb-h-12 srb-flex-shrink-0 srb-bg-white srb-overflow-hidden">
                                      <img
                                        src={
                                          compatibilityData.wheelbaseAdapter
                                            .images[0].image
                                        }
                                        alt={
                                          compatibilityData.wheelbaseAdapter
                                            .name
                                        }
                                        className="srb-w-full srb-h-full srb-object-contain"
                                      />
                                    </div>
                                  )}
                                  <div className="srb-flex srb-flex-col">
                                    <div className="srb-font-semibold srb-text-main srb-text-sm srb-truncate">
                                      {compatibilityData.wheelbaseAdapter.name}
                                    </div>
                                    <div className="srb-text-xs srb-text-muted">
                                      {
                                        compatibilityData.wheelbaseAdapter.brand
                                          .name
                                      }
                                    </div>
                                    <div className="srb-font-bold srb-text-main srb-text-sm">
                                      {formatPrice(
                                        compatibilityData.wheelbaseAdapter
                                          .price,
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {compatibilityData.wheelAdapter && (
                          <div
                            className="srb-p-3 srb-border srb-border-secondary"
                            style={(function () {
                              const root =
                                document.querySelector(".sim-rig-builder");
                              if (!root) return {};
                              const borderColor = getComputedStyle(root)
                                .getPropertyValue("--srb-secondary-color")
                                .trim();
                              return { boxShadow: `0 0 0 1px ${borderColor}` };
                            })()}
                          >
                            <div className="srb-flex srb-gap-2">
                              <div className="srb-flex-1 srb-min-w-0">
                                <div className="srb-text-xs srb-text-yellow-400 srb-uppercase srb-tracking-wider srb-mb-1 srb-font-semibold">
                                  WHEEL ADAPTER
                                </div>
                                <div className="srb-flex srb-items-center srb-gap-2">
                                  {compatibilityData.wheelAdapter.images[0] && (
                                    <div className="srb-relative srb-w-12 srb-h-12 srb-flex-shrink-0 srb-bg-white srb-overflow-hidden">
                                      <img
                                        src={
                                          compatibilityData.wheelAdapter
                                            .images[0].image
                                        }
                                        alt={
                                          compatibilityData.wheelAdapter.name
                                        }
                                        className="srb-w-full srb-h-full srb-object-contain"
                                      />
                                    </div>
                                  )}
                                  <div className="srb-flex srb-flex-col">
                                    <div className="srb-font-semibold srb-text-main srb-text-sm srb-truncate">
                                      {compatibilityData.wheelAdapter.name}
                                    </div>
                                    <div className="srb-text-xs srb-text-muted">
                                      {
                                        compatibilityData.wheelAdapter.brand
                                          .name
                                      }
                                    </div>
                                    <div className="srb-font-bold srb-text-main srb-text-sm">
                                      {formatPrice(
                                        compatibilityData.wheelAdapter.price,
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : compatibilityData.compatible ? (
                  <div
                    className="srb-p-3 srb-border srb-border-green-600 srb-bg-background"
                    style={{ boxShadow: "0 0 0 1px #45e436ff" }}
                  >
                    <div className="srb-flex srb-items-start srb-gap-2">
                      <CheckLine
                        className="srb-text-green-500 srb-flex-shrink-0 srb-mt-0.5"
                        size={16}
                      />
                      <div>
                        <div className="srb-text-sm srb-font-semibold srb-text-main">
                          Wheel and Wheelbase Compatible
                        </div>
                        <div className="srb-text-xs srb-text-main srb-mt-1">
                          {compatibilityData.message ||
                            `Using ${compatibilityData.interfaceUsed} connection`}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="srb-p-3 srb-border srb-border-red-600 srb-bg-background"
                    style={{ boxShadow: "0 0 0 1px #ff0000ff" }}
                  >
                    <div className="srb-flex srb-items-start srb-gap-2">
                      <X
                        className="srb-text-red-500 srb-flex-shrink-0 srb-mt-0.5"
                        size={16}
                      />
                      <div>
                        <div className="srb-text-sm srb-font-semibold srb-text-main">
                          Wheel and wheelbase not compatible
                        </div>
                        <div className="srb-text-xs srb-text-main srb-mt-1">
                          {compatibilityData.message ||
                            "These components are not compatible"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="srb-border-t srb-border-subtle srb-pt-4">
        <div className="srb-flex srb-justify-between srb-items-center srb-mb-2">
          <span className="srb-text-muted">Components:</span>
          <span className="srb-font-semibold srb-text-main">
            {componentCount}
          </span>
        </div>
        <div className="srb-flex srb-justify-between srb-items-center srb-mb-4">
          <span className="srb-text-xl srb-font-bold srb-text-main">
            Total:
          </span>
          <span className="srb-text-2xl srb-font-bold srb-text-main">
            {formatPrice(totalPrice)}
          </span>
        </div>

        <div className="srb-flex srb-gap-2">
          <button
            className="srb-cursor-pointer srb-flex srb-items-center srb-justify-center srb-border srb-border-subtle !srb-bg-transparent srb-text-main srb-py-3 srb-px-4 hover:srb-bg-hover srb-font-semibold srb-transition"
            onClick={() => setShareModalOpen(true)}
            title="Share Build"
          >
            <Share2 size={18} />
          </button>
          <button
            className="srb-cursor-pointer srb-flex srb-flex-1 srb-items-center srb-justify-center srb-border-none srb-text-white srb-py-3 srb-bg-primary hover:srb-opacity-90 srb-font-semibold srb-transition"
            onClick={handleContinue}
          >
            Where to Buy
            <ArrowRight className="srb-inline-block srb-ml-2" />
          </button>
        </div>
      </div>

      <div className="srb-mt-6 srb-pt-6 srb-border-t srb-border-secondary srb-text-center">
        <p className="srb-text-main srb-flex srb-items-center srb-justify-center srb-gap-4 srb-mt-1 srb-text-sm srb-italic srb-font-semibold">
          Powered by{" "}
          <a
            href="https://build.readysetsim.com"
            target="_blank"
            rel="noopener noreferrer"
            className="srb-font-semibold srb-underline"
          >
            <img
              src="https://build.readysetsim.com/images/logos/rss_logo2.svg"
              alt="Ready Set Sim"
              className={iconClasses}
              draggable="false"
            />
          </a>
        </p>
      </div>

      <PricingDetailsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedComponents={selectedComponents}
        compatibilityData={compatibilityData}
      />

      <ShareBuildModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        selectedComponents={selectedComponents}
        onLoadBuild={onLoadBuild || (() => {})}
      />
    </div>
  );
};
